'use client'

import { datadogLogs, type LogsInitConfiguration } from '@datadog/browser-logs'
import { datadogRum, type RumInitConfiguration } from '@datadog/browser-rum'
import { useEffect } from 'react'

import { MomentEnv } from '@/app/moment-env'

import { buildDatadogProxyUrl } from './helpers/build-datadog-proxy-url'
import { getEntityViewName } from './helpers/get-entity-view-name'
import { redactLogsEvent } from './helpers/redact-logs-event'
import { redactRumEvent } from './helpers/redact-rum-event'

const baseInitConfig = {
  // set 'empty' to force datadog to still init if unset,
  // as this will set up the logger without sending remote logs.
  clientToken: MomentEnv.DataDogClientToken ?? 'empty',
  site: 'datadoghq.com',
  service: MomentEnv.DataDogService,
  env: MomentEnv.DataDogEnv,
  version: MomentEnv.GitHash,
  // Ad-blocker avoidance: intake requests go same-origin through the static
  // rewrites in next.config.js instead of straight to Datadog.
  proxy: buildDatadogProxyUrl,
} as const

// Browser profiling is metered per profiled session and samples the main
// thread every 10ms, so it stays on staging until we've sized the cost
// (PF-2675). Multiplies with sessionSampleRate (unset here → 100). Also
// requires the Document-Policy header set in next.config.js — without it the
// browser never exposes the Profiler API and the SDK silently no-ops.
const profilingSampleRate = baseInitConfig.env === 'staging' ? 100 : 0

// SORM requests are routed to their own host when NEXT_PUBLIC_SORM_API_ROOT
// is set, and they carry most of the app's read traffic (/auth/v1/sorm/query).
// Both hosts must be listed or SORM requests get no RUM→APM trace correlation.
// Deduped (the two vars usually point at the same host) and empty values
// dropped — an empty `match` string would match every URL.
const tracedApiUrls = [
  ...new Set([MomentEnv.ApiUrl, MomentEnv.SormApiUrl]),
].filter((url): url is string => url !== undefined && url !== '')

const rumInitConfig: RumInitConfiguration = {
  ...baseInitConfig,
  applicationId: MomentEnv.DataDogApplicationId ?? '',
  sessionReplaySampleRate: 100,
  profilingSampleRate,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'mask-user-input',
  // v7 defaults this to true, which appends a `baggage` header to traced
  // cross-origin requests; our API's CORS allowlist does not accept it yet, so
  // those requests would fail. Keep off until the backend allows `baggage`.
  propagateTraceBaggage: false,
  // v7 defaults this to true, masking computed action names per
  // defaultPrivacyLevel. Keep off to preserve v6 action-name collection.
  enablePrivacyForActionName: false,
  // Specify URLs to propagate trace headers for connection between RUM and backend trace
  allowedTracingUrls: tracedApiUrls.map((url) => ({
    match: url,
    propagatorTypes: ['tracecontext'],
  })),
  // Rename entity-discriminated views (e.g. /views?entity=account →
  // /views/account) so each table reports as its own RUM view. Applied to
  // every event type so `@view.name` filters cover resources/actions/errors
  // too.
  beforeSend: (event) => {
    // Benign, spec-mandated browser noise — see "ResizeObserver loop" in
    // discoveries.md.
    if (
      event.type === 'error' &&
      event.error.message.includes('ResizeObserver loop')
    ) {
      return false
    }
    const entityViewName = getEntityViewName(event.view?.url)
    if (entityViewName !== undefined && event.view !== undefined) {
      event.view.name = entityViewName
    }
    redactRumEvent(event)
    // RUM has no DOM-size attribute, so record it on each click. Not on custom
    // actions: addAction runs this inside the caller's event handler. Skip
    // clicks sent after the user moved to another view: the DOM may already be
    // the next view's.
    if (event.type === 'action' && event.action.type === 'click') {
      const actionView = entityViewName ?? new URL(event.view.url).pathname
      const currentView =
        getEntityViewName(window.location.href) ?? window.location.pathname
      if (actionView === currentView) {
        event.context = {
          ...event.context,
          dom_elements: document.getElementsByTagName('*').length,
        }
      }
    }
    return true
  },
}

const logsInitConfig: LogsInitConfiguration = {
  ...baseInitConfig,
  forwardErrorsToLogs: true,
  forwardConsoleLogs: ['warn', 'error'],
  beforeSend: (event) => {
    redactLogsEvent(event)
    return true
  },
}

// Guard against re-running init across remounts (e.g. HMR or Suspense).
let hasLogsInitialized = false
let hasRumInitialized = false

// Schedules work for after first paint so SDK setup — for RUM, the
// session-replay recorder chunk and initial DOM serialization — doesn't compete
// with cold-load work on the main thread. requestIdleCallback runs it once the
// browser is idle; the setTimeout fallback covers browsers without it (e.g.
// Safari).
function scheduleAfterPaint(run: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {}
  }

  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(run)
    return () => window.cancelIdleCallback(handle)
  }

  const handle = window.setTimeout(run, 0)
  return () => window.clearTimeout(handle)
}

// Returns a cleanup that cancels the pending schedule. setUser/clearUser calls
// made before init are buffered by the SDK and applied once init runs.
export function initDatadogRum(): () => void {
  if (rumInitConfig.applicationId === '') {
    return () => {}
  }

  return scheduleAfterPaint(() => {
    if (hasRumInitialized) {
      return
    }
    hasRumInitialized = true
    datadogRum.init(rumInitConfig)
  })
}

function initDatadogLogs(): () => void {
  return scheduleAfterPaint(() => {
    if (hasLogsInitialized) {
      return
    }
    hasLogsInitialized = true
    datadogLogs.init(logsInitConfig)
  })
}

// Renders nothing — initializes Datadog browser logs client-side, deferred past
// first paint via useEffect. Logs cover unauthenticated pages too, so this is
// mounted at the root layout; RUM starts separately via initDatadogRum.
// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function DatadogProvider(): React.ReactNode {
  useEffect(() => initDatadogLogs(), [])

  return null
}
